import { useState, useEffect, useCallback } from 'react';
import { AuditChatFilter } from './AuditChatFilter';

export interface AuditFilterState {
  searchQuery: string;
  eventCategory: string;
  eventType: string;
  actorId: string;
  startDate: string;
  endDate: string;
  timeRange: string;
  /** Chat (conversation) id to scope the log to — the functional filter. */
  conversationId: string;
  /** Title of the filtered chat — kept for chip / control labels only. */
  conversationTitle: string;
}

export const EMPTY_FILTERS: AuditFilterState = {
  searchQuery: '',
  eventCategory: '',
  eventType: '',
  actorId: '',
  startDate: '',
  endDate: '',
  timeRange: '',
  conversationId: '',
  conversationTitle: '',
};

interface AuditFiltersProps {
  filters: AuditFilterState;
  onChange: (filters: AuditFilterState) => void;
}

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'conversation', label: 'Chat' },
  { value: 'system_configuration', label: 'System Configuration' },
  { value: 'task_agent', label: 'Task Agent' },
  { value: 'security_event', label: 'Security Event' },
  { value: 'data_access', label: 'Data Access' },
];

const TIME_RANGES = [
  { value: '', label: 'All Time' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range' },
];

function computeDateRange(range: string): { startDate: string; endDate: string } {
  if (!range || range === 'custom') return { startDate: '', endDate: '' };
  const now = new Date();
  const end = now.toISOString();
  let start: Date;
  switch (range) {
    case '24h': start = new Date(now.getTime() - 24 * 60 * 60 * 1000); break;
    case '7d': start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
    case '30d': start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); break;
    default: return { startDate: '', endDate: '' };
  }
  return { startDate: start.toISOString(), endDate: end };
}

export function AuditFilters({ filters, onChange }: AuditFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.searchQuery);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.searchQuery) {
        onChange({ ...filters, searchQuery: searchInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // intentionally only re-run on searchInput change

  const updateFilter = useCallback((key: keyof AuditFilterState, value: string) => {
    const next = { ...filters, [key]: value };
    // Auto-compute date range for presets
    if (key === 'timeRange' && value !== 'custom') {
      const { startDate, endDate } = computeDateRange(value);
      next.startDate = startDate;
      next.endDate = endDate;
    }
    onChange(next);
  }, [filters, onChange]);

  const hasActiveFilters = filters.eventCategory || filters.eventType || filters.actorId ||
    filters.startDate || filters.endDate || filters.searchQuery || filters.conversationId;

  const categoryLabel = CATEGORIES.find(c => c.value === filters.eventCategory)?.label ?? filters.eventCategory;

  const activeChips: Array<{ key: keyof AuditFilterState; label: string }> = [];
  if (filters.eventCategory) activeChips.push({ key: 'eventCategory', label: `Category: ${categoryLabel}` });
  if (filters.eventType) activeChips.push({ key: 'eventType', label: `Type: ${filters.eventType}` });
  if (filters.conversationId) activeChips.push({ key: 'conversationId', label: `Chat: ${filters.conversationTitle || filters.conversationId}` });
  if (filters.actorId) activeChips.push({ key: 'actorId', label: `Actor: ${filters.actorId}` });
  if (filters.timeRange && filters.timeRange !== 'custom') activeChips.push({ key: 'timeRange', label: `Time: ${TIME_RANGES.find(t => t.value === filters.timeRange)?.label ?? filters.timeRange}` });
  if (filters.searchQuery) activeChips.push({ key: 'searchQuery', label: `Search: ${filters.searchQuery}` });

  return (
    <div>
      <div className="audit-filter-row">
        <div className="audit-filter-field">
          <span className="audit-filter-label">Search</span>
          <div className="audit-search-wrapper">
            <span className="audit-search-icon">{'⌕'}</span>
            <input
              type="text"
              className="audit-search-input"
              placeholder="Search audit records..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
        </div>

        <div className="audit-filter-field">
          <span className="audit-filter-label">Category</span>
          <select
            className="audit-filter-select"
            value={filters.eventCategory}
            onChange={e => updateFilter('eventCategory', e.target.value)}
          >
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <AuditChatFilter
          conversationId={filters.conversationId}
          conversationTitle={filters.conversationTitle}
          onChange={v => onChange({ ...filters, ...v })}
        />

        <div className="audit-filter-field">
          <span className="audit-filter-label">Time Range</span>
          <select
            className="audit-filter-select"
            value={filters.timeRange}
            onChange={e => updateFilter('timeRange', e.target.value)}
          >
            {TIME_RANGES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {filters.timeRange === 'custom' && (
          <>
            <div className="audit-filter-field">
              <span className="audit-filter-label">From</span>
              <input
                type="date"
                className="audit-filter-input"
                value={filters.startDate ? filters.startDate.slice(0, 10) : ''}
                onChange={e => updateFilter('startDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
              />
            </div>
            <div className="audit-filter-field">
              <span className="audit-filter-label">To</span>
              <input
                type="date"
                className="audit-filter-input"
                value={filters.endDate ? filters.endDate.slice(0, 10) : ''}
                onChange={e => updateFilter('endDate', e.target.value ? new Date(e.target.value + 'T23:59:59.999Z').toISOString() : '')}
              />
            </div>
          </>
        )}

        <div className="audit-filter-field">
          <span className="audit-filter-label">Actor</span>
          <input
            type="text"
            className="audit-filter-input"
            placeholder="User ID or email"
            value={filters.actorId}
            onChange={e => updateFilter('actorId', e.target.value)}
          />
        </div>

        {hasActiveFilters && (
          <button className="audit-clear-filters" onClick={() => { setSearchInput(''); onChange(EMPTY_FILTERS); }}>
            Clear all
          </button>
        )}
      </div>

      {activeChips.length > 0 && (
        <div className="audit-filter-chips">
          {activeChips.map(chip => (
            <span key={chip.key} className="audit-filter-chip">
              {chip.label}
              <button
                className="audit-filter-chip-remove"
                onClick={() => {
                  if (chip.key === 'searchQuery') {
                    setSearchInput('');
                    updateFilter('searchQuery', '');
                  } else if (chip.key === 'timeRange') {
                    onChange({ ...filters, timeRange: '', startDate: '', endDate: '' });
                  } else if (chip.key === 'conversationId') {
                    onChange({ ...filters, conversationId: '', conversationTitle: '' });
                  } else {
                    updateFilter(chip.key, '');
                  }
                }}
              >
                {'×'}
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
